/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraFoundationNodeChildMap,
  GamoraFoundationNodeConstructor,
  GamoraFoundationNodeRoot,
} from '../foundation/node/node.js';
import {
  GamoraUtilsStrand,
  GamoraUtilsStrandPromise,
} from '../utils/strand.js';
import { assert } from '../utils/assert.js';
import { createUUIDv4 } from '../utils/uuid.js';
import { debugCreate } from '../utils/debug.js';
import { GamoraArgsType } from '../foundation/generic.js';
import { GamoraControllerStateConstructor } from './states/state.js';
import { GamoraFoundationGlue } from '../foundation/glue/glue.js';
const debug = debugCreate({ namespace: 'gamora:controller' });

/**
 * GamoraControllerStateMapEntry
 *
 * A nodemap entry
 * @public
 */
export type GamoraControllerStateMapEntry = GamoraControllerStateConstructor;

/**
 * GamoraControllerStateMap
 *
 * A record of all nodes of a base type
 * @public
 */
export type GamoraControllerStateMap = {
  readonly [stateCurrent: string]: GamoraControllerStateMapEntry;
};

/**
 * GamoraControllerTransitionMapEntry
 *
 * Entry of allowed state transitions
 * @public
 */
export type GamoraControllerTransitionMapEntry<
  TStateMap extends GamoraFoundationNodeChildMap<TStateMap>,
> = readonly GamoraControllerStateName<TStateMap>[];

/**
 * GamoraControllerTransitionMap
 *
 * List of allowed state transitions
 * @public
 */
export type GamoraControllerTransitionMap<
  TStateMap extends GamoraFoundationNodeChildMap<TStateMap>,
> = {
  [state in GamoraControllerStateName<TStateMap>]: GamoraControllerTransitionMapEntry<TStateMap>;
};

/**
 * GamoraControllerStateContext
 *
 * The state context
 * @public
 */
export type GamoraControllerStateContext<
  TStateContextRegistry extends Record<string, any> = Record<string, any>,
> = {
  contextIdentifier: GamoraControllerContextIdentifier;
  stateContextRegistry: TStateContextRegistry;
};

/**
 * GamoraControllerContextIdentifier
 *
 * The context identifier
 * @public
 */
export type GamoraControllerContextIdentifier = string;

/**
 * GamoraControllerStateName
 *
 * The state: equals a key in the childmap
 * @public
 */
export type GamoraControllerStateName<
  TStateMap extends GamoraFoundationNodeChildMap<TStateMap>,
> = keyof TStateMap;

/**
 * GamoraControllerContext
 *
 * The controller context
 * @public
 */
export type GamoraControllerContext<
  TStateMap extends GamoraControllerStateMap,
  TStateContext = GamoraControllerStateContext<any>,
> = {
  contextIdentifier: GamoraControllerContextIdentifier;
  transitionActive: boolean;
  transitionPosted: boolean;
  contextActive: boolean;
  contextStrand: GamoraUtilsStrand;
  stateContext: TStateContext;
  stateCurrent: GamoraControllerStateName<TStateMap> | null;
  stateNext: GamoraControllerStateName<TStateMap> | null;
  defaultStateStart: GamoraControllerStateName<TStateMap>;
  defaultStateAbort: GamoraControllerStateName<TStateMap>;
};

/**
 * GamoraControllerArgs
 *
 * The controller arguments
 * @public
 */
export interface GamoraControllerArgs<
  TStateMap extends GamoraFoundationNodeChildMap<TStateMap>,
> {
  root?: GamoraFoundationNodeRoot;
  stateMap: GamoraControllerStateMapArgs<TStateMap>;
  transitionMap: GamoraControllerTransitionMap<TStateMap>;
}

/**
 * GamoraControllerArgsType
 *
 * Used internally to obtain the arguments of a node map entry
 * @internal
 */
export type GamoraControllerArgsType<
  TArgsType extends GamoraFoundationNodeConstructor,
> = GamoraArgsType<TArgsType>;

/**
 * GamoraControllerStateMapArgs
 * @public
 */
export type GamoraControllerStateMapArgs<
  TStateMap extends GamoraFoundationNodeChildMap<TStateMap>,
> = {
  readonly [k in keyof TStateMap]: readonly [
    TStateMap[k],
    GamoraControllerArgsType<TStateMap[k]>,
  ];
};

/**
 * Input type for create context
 * @public
 */
export type GamoraControllerCreateContextInput<
  TStateMap extends GamoraFoundationNodeChildMap<TStateMap>,
> = {
  defaultStateStart: GamoraControllerStateName<TStateMap>;
  defaultStateAbort: GamoraControllerStateName<TStateMap>;
  contextIdentifier?: GamoraControllerContextIdentifier;
};

/**
 * Input type for abort context
 * @public
 */
export type GamoraControllerAbortContextInput<
  TStateMap extends GamoraFoundationNodeChildMap<TStateMap>,
> = {
  stateAbort?: keyof TStateMap;
};

/**
 * Input type for start context
 * @public
 */
export type GamoraControllerStartContextInput<
  TStateMap extends GamoraFoundationNodeChildMap<TStateMap>,
> = {
  stateStart?: keyof TStateMap;
};

/**
 * Input type for transition context
 * @public
 */
export type GamoraControllerTransitionContextInput<
  TStateMap extends GamoraFoundationNodeChildMap<TStateMap>,
> = {
  stateNext: GamoraControllerStateName<TStateMap>;
};

/**
 * GamoraController
 *
 * Abstract class that provides a state machine manager
 * @public
 */
export abstract class GamoraController<
  TStateMap extends GamoraControllerStateMap,
  TStateContext extends GamoraControllerStateContext,
  TRoot extends GamoraFoundationNodeRoot = any,
> extends GamoraFoundationGlue<TStateMap, TRoot> {
  protected _$transitionMap: GamoraControllerTransitionMap<TStateMap>;
  protected _$contexts: Map<
    GamoraControllerContextIdentifier,
    GamoraControllerContext<TStateMap, TStateContext>
  >;

  /**
   * Constructor
   */
  constructor(args: GamoraControllerArgs<TStateMap>) {
    super({
      childMap: args.stateMap,
      root: args.root,
    });
    this._$transitionMap = args.transitionMap;
    this._$contexts = new Map();
    this._$registerStates();
  }

  /**
   * Register events for states
   */
  private _$registerStates() {
    for (const key of this._$children.keys()) {
      const state = this.$(key);
      state.$controller = this;
    }
  }

  /**
   * Generates a simple contextIdentifier
   */
  protected _$generateContextIdentifier(): GamoraControllerContextIdentifier {
    return createUUIDv4();
  }

  /**
   * Gets state of contextIdentifier
   * @param contextIdentifier - The controller state contextIdentifier
   */
  $getContext(
    contextIdentifier: GamoraControllerContextIdentifier,
  ): GamoraControllerContext<TStateMap, TStateContext> {
    assert(
      typeof contextIdentifier === 'string' && contextIdentifier.length > 0,
      'Invalid contextIdentifier argument: not a valid string.',
    );
    const context = this._$contexts.get(contextIdentifier);
    assert(
      context !== undefined,
      'Invalid contextIdentifier argument: contextIdentifier does not exist.',
    );
    return context;
  }

  /**
   * Checks if context identifier exists
   * @param contextIdentifier - The controller state contextIdentifier
   */
  $hasContext(contextIdentifier: GamoraControllerContextIdentifier): boolean {
    assert(
      typeof contextIdentifier === 'string' && contextIdentifier.length > 0,
      'Invalid contextIdentifier argument: not a valid string.',
    );
    const context = this._$contexts.get(contextIdentifier);
    return context !== undefined;
  }

  /**
   * Creates a new statemachine context
   */
  $createContext<TInput extends GamoraControllerCreateContextInput<TStateMap>>(
    input: TInput,
  ): GamoraControllerContextIdentifier {
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not a valid object.',
    );
    assert(
      typeof input.defaultStateStart === 'string',
      'Invalid defaultStateStart argument: not a string.',
    );
    assert(
      typeof input.defaultStateAbort === 'string',
      'Invalid defaultStateAbort argument: not a string.',
    );
    assert(
      Array.from(this._$children.keys()).includes(input.defaultStateStart),
      'Invalid defaultStateStart argument: not a valid state.',
    );
    assert(
      Array.from(this._$children.keys()).includes(input.defaultStateAbort),
      'Invalid defaultStateAbort argument: not a valid state.',
    );

    // Validation of states
    const allowedStartTransitions =
      this._$transitionMap[input.defaultStateStart];
    const allowedStopTransitions =
      this._$transitionMap[input.defaultStateAbort];
    assert(
      allowedStartTransitions.length > 0,
      'Invalid defaultStateStart argument: no state transitions are allowed in this state.',
    );
    assert(
      allowedStopTransitions.length === 0,
      'Invalid defaultStateAbort argument: possible state transitions left in this state.',
    );

    // Generate contextIdentifier
    const contextIdentifier =
      input.contextIdentifier ?? this._$generateContextIdentifier();

    // Improbable, but lets check
    assert(
      !this._$contexts.has(contextIdentifier),
      'Invalid contextIdentifier: failed to generate unique contextIdentifier.',
    );

    // Create controller context
    const context: GamoraControllerContext<TStateMap, TStateContext> = {
      contextActive: false,
      transitionActive: false,
      transitionPosted: false,
      contextStrand: new GamoraUtilsStrand(),
      contextIdentifier: contextIdentifier,
      stateCurrent: null,
      stateNext: null,
      defaultStateAbort: input.defaultStateAbort,
      defaultStateStart: input.defaultStateStart,
      stateContext: {
        contextIdentifier: contextIdentifier,
        stateContextRegistry: {},
      } as TStateContext,
    };

    // Start the strand
    context.contextStrand.start();

    // Set context
    this._$contexts.set(contextIdentifier, context);

    // Returns contextIdentifier
    return contextIdentifier;
  }

  /**
   * Abort context if exists
   * @note Can always be invoked even when there are already transitions pending
   * @param contextIdentifier - The controller state contextIdentifier
   */
  $tryAbortContext<TInput extends GamoraControllerAbortContextInput<TStateMap>>(
    contextIdentifier: GamoraControllerContextIdentifier,
    input: TInput,
  ): GamoraUtilsStrandPromise<void> | undefined {
    assert(
      typeof contextIdentifier === 'string' && contextIdentifier.length > 0,
      'Invalid contextIdentifier argument: not a valid string.',
    );
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not a valid object.',
    );
    if (this._$contexts.has(contextIdentifier)) {
      const context = this._$contexts.get(contextIdentifier)!;
      return this.$transitionContext(contextIdentifier, {
        stateNext: input.stateAbort ?? context.defaultStateAbort,
      });
    }
  }

  /**
   * Abort
   * @note Can always be invoked even when there are already transitions pending
   * @param contextIdentifier - The controller state contextIdentifier
   */
  $abortContext<TInput extends GamoraControllerAbortContextInput<TStateMap>>(
    contextIdentifier: GamoraControllerContextIdentifier,
    input: TInput,
  ): GamoraUtilsStrandPromise<void> {
    assert(
      typeof contextIdentifier === 'string' && contextIdentifier.length > 0,
      'Invalid contextIdentifier argument: not a valid string.',
    );
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not a valid object.',
    );
    const context = this._$contexts.get(contextIdentifier)!;
    return this.$transitionContext(contextIdentifier, {
      stateNext: input.stateAbort ?? context.defaultStateAbort,
    });
  }

  /**
   * Start
   * @note Can always be invoked even when there are already transitions pending
   * @param contextIdentifier - The controller state contextIdentifier
   */
  $startContext<TInput extends GamoraControllerStartContextInput<TStateMap>>(
    contextIdentifier: GamoraControllerContextIdentifier,
    input: TInput,
  ): GamoraUtilsStrandPromise<void> {
    assert(
      typeof contextIdentifier === 'string' && contextIdentifier.length > 0,
      'Invalid contextIdentifier argument: not a valid string.',
    );
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not a valid object.',
    );
    const context = this._$contexts.get(contextIdentifier)!;

    // Ensure context is not active
    assert(
      !context.contextActive,
      'Invalid context: cannot start context that is already active.',
    );
    return context.contextStrand.post(async () => {
      const stateStart = input.stateStart ?? context.defaultStateStart;

      // Ensure context is not active
      assert(
        !context.contextActive,
        'Invalid context: cannot start context that is already active.',
      );

      // Transitioning
      assert(
        !context.transitionActive,
        'Invalid context: already in transition.',
      );
      context.transitionActive = true;

      try {
        // Controller is now active
        context.contextActive = true;

        // Switch state
        context.stateCurrent = stateStart;
        debug.log(
          '(transition): Controller context changing state to ' +
            contextIdentifier +
            '/' +
            stateStart.toString() +
            '.',
        );

        // Enter new state
        await this.$(stateStart).$enter(context.stateContext);
      } catch (e: any) {
        // Any error in transition should be reported
        debug.trace(e?.message, e?.stack);

        // Runs cleanup
        try {
          for (const key of this._$children.keys()) {
            const state = this.$(key);
            state.$cleanup(context.stateContext);
          }
        } finally {
          // Remove from contexts
          this._$contexts.delete(contextIdentifier);

          // Context finsihed
          context.contextActive = false;

          // Stop execution strand
          context.contextStrand.stop();
          debug.log(
            '(transition): Controller context finished with errors for ' +
              contextIdentifier +
              '.',
          );
        }
      } finally {
        context.transitionActive = false;
      }
    });
  }

  /**
   * Transition to new state
   * @note Can always be invoked even when there are already transitions pending
   * @param contextIdentifier - The controller state contextIdentifier
   * @param to - The new state
   */
  $transitionContext<
    TInput extends GamoraControllerTransitionContextInput<TStateMap>,
  >(
    contextIdentifier: GamoraControllerContextIdentifier,
    input: TInput,
  ): GamoraUtilsStrandPromise<void> {
    assert(
      typeof contextIdentifier === 'string' && contextIdentifier.length > 0,
      'Invalid contextIdentifier argument: not a valid string.',
    );
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not a valid object.',
    );
    assert(
      typeof input.stateNext === 'string',
      'Invalid stateNext argument: not a string.',
    );
    assert(
      Array.from(this._$children.keys()).includes(input.stateNext),
      'Invalid stateNext argument: not a valid state.',
    );
    assert(
      this._$contexts.has(contextIdentifier),
      'Invalid contextIdentifier argument: contextIdentifier does not exist.',
    );
    const context = this._$contexts.get(
      contextIdentifier,
    ) as GamoraControllerContext<TStateMap, TStateContext>;

    // Only transitions for active context
    assert(context.contextActive, 'Invalid context: context is not active.');

    // Execute transition on strand
    return context.contextStrand.post(async () => {
      // Make sure context exists
      assert(
        this._$contexts.has(contextIdentifier),
        'Invalid context in strand: contextIdentifier does not exist.',
      );

      // Allows transitions
      const stateFrom = context.stateCurrent;
      assert(stateFrom !== null, 'Invalid stateFrom: cannot be null.');
      const allowedTransitions = this._$transitionMap[stateFrom];
      const allowedNextTransitions = this._$transitionMap[input.stateNext];

      // Ensure valid transitions
      assert(
        Array.isArray(allowedTransitions),
        'Invalid to argument: not a valid transitional state.',
      );
      assert(
        Array.isArray(allowedNextTransitions),
        'Invalid to argument: not a valid transitional state.',
      );

      // Set transition active
      assert(
        !context.transitionActive,
        'Invalid context: already in transition.',
      );
      context.transitionActive = true;

      // Set the name of the next context
      assert(!context.stateNext, 'Invalid context: next state already set.');
      context.stateNext = input.stateNext;

      // Only transitions for active context
      assert(context.contextActive, 'Invalid context: context is not active.');

      // Transition
      try {
        assert(
          allowedTransitions.includes(input.stateNext),
          'Invalid to argument: not allowed to transition into this state.',
        );

        debug.log(
          '(transition): Controller context changing state from ' +
            contextIdentifier +
            '/' +
            stateFrom.toString() +
            ' to ' +
            contextIdentifier +
            '/' +
            input.stateNext.toString() +
            '.',
        );

        // Exit current state
        await this.$(stateFrom).$exit(context.stateContext);

        // Switch state
        context.stateCurrent = input.stateNext;
        context.stateNext = null;

        // Stop criterion
        if (allowedNextTransitions.length === 0) {
          // Enter last state
          await this.$(input.stateNext).$enter(context.stateContext);

          // Exit last state
          await this.$(input.stateNext).$exit(context.stateContext);

          // Runs cleanup
          for (const key of this._$children.keys()) {
            const state = this.$(key);
            state.$cleanup(context.stateContext);
          }

          // Remove from contexts
          this._$contexts.delete(contextIdentifier);

          // Context finished
          context.contextActive = false;
          context.transitionPosted = false;

          // Stop executing strand
          context.contextStrand.stop();

          // Finished
          debug.log(
            '(transition): Controller context finished at ' +
              contextIdentifier +
              '/' +
              input.stateNext.toString() +
              '.',
          );
        } else {
          // Continue to allow strand posts
          context.transitionPosted = false;

          // Enter new state
          await this.$(input.stateNext).$enter(context.stateContext);
        }
      } catch (e: any) {
        // Any error in transition should be reported
        debug.trace(e?.message, e?.stack);

        // Runs cleanup
        try {
          for (const key of this._$children.keys()) {
            const state = this.$(key);
            state.$cleanup(context.stateContext);
          }
        } finally {
          // Remove from contexts
          this._$contexts.delete(contextIdentifier);

          // Context finsihed
          context.contextActive = false;
          context.transitionPosted = false;

          // Stop execution strand
          context.contextStrand.stop();
          debug.log(
            '(transition): Controller context finished with errors for ' +
              contextIdentifier +
              '.',
          );
        }
      } finally {
        // Transition finished
        context.transitionActive = false;
      }
    });
  }
}
